<template>
    <div
        class="payment-summary payment-summary__card-gap"
        :class="countryClass"
        data-ci="payment-summary__promo">
        <template v-if="displayTrialInfo">
            <div class="payment-summary__two-col" data-ci="payment-summary__trialString">
                <div class="col">
                    {{ pageAttr.billing_summary_trial_label }}
                </div>
                <div class="col qt-trial-price">
                    {{ coupon.trialString || (plan && plan.trialString) }}
                </div>
            </div>
        </template>
        <template v-if="promoString">
            <div
                :class="[
                    'payment-summary__two-col',
                    displayTrialInfo ? 'payment-summary__single-col-gap' : '',
                ]"
                data-ci="payment-summary__discountString">
                <div class="col">
                    {{ isFTC ? pageAttr.billing_summary_promotion_label : $getLocale('discount') }}
                </div>
                <div class="col qt-discount-price">
                    {{ promoString }}
                </div>
                <template v-if="afterPromoDescription">
                    <div></div>
                    <div class="payment-summary__after-promo">{{ afterPromoDescription }}</div>
                </template>
            </div>
            <div
                v-if="!isFTC"
                class="payment-summary__details-text payment-summary__single-col-gap-details">
                <div class="payment-summary__text-strikethrough">{{ internationalizedPrice }}</div>
            </div>
            <div class="payment-summary__details-text">
                <div data-testid="subtotalText" v-show="showSubTotalNow && !isFTC">
                    {{ this.formatSubTotalText }}
                </div>
            </div>
        </template>
        <template v-if="showCouponField">
            <div class="payment-summary__section-border" v-if="displayBorderLine"></div>
            <button
                class="payment-summary__coupon-prompt"
                :aria-expanded="couponFieldOpenState ? true : false"
                @click="handleClickCouponPrompt">
                <div class="prompt qt-promo-link" tabindex="0">
                    {{ pageAttr.billing_summary_coupon_field_prompt }}
                </div>
                <div :class="iconCaretClassList"></div>
            </button>

            <transition name="slide-down-fade">
                <div
                    v-if="couponFieldManualOpen || !$store.state.promo || autoPromoChecked"
                    v-show="couponFieldOpenState">
                    <div class="payment-summary__coupon">
                        <InputField
                            :key="getCouponCodeValue"
                            :inputConfig="getCouponInputConfig"
                            v-model="form['couponCode']"
                            :class="{ error: coupon.errorMessage }"
                            :keydownHandler="keydownHandler"></InputField>
                        <InputElm
                            v-show="displayPromoField"
                            ref="planRecurly"
                            :inputConfig="orderSummaryConfig.hiddenInputList.plan" />

                        <div class="payment-summary__coupon__error qt-err-coupon-code">
                            {{ coupon.errorMessage }}
                        </div>
                        <ButtonCta :buttonConfig="buttonConfig"></ButtonCta>
                    </div>
                </div>
            </transition>
        </template>
    </div>
</template>

<script>
    import InputElm from 'atoms/InputElm';
    import InputField from 'atoms/InputField';
    import ButtonCta from 'atoms/ButtonCta';
    import { mapState } from 'vuex';
    import pricePrettyPrint from 'aa/vue/plugins/priceMixin';
    import aarpMixin from 'aa/vue/plugins/aarpMixin.js';
    import { capitalize } from 'aa/vue/components/account/helpers';
    import { pluralizeDuration } from 'aa/helpers/stringManipulation';
    import Product from 'aa/vue/models/Product';
    import {
        getAfterPromoString,
        getDurationString,
        getOfferPriceString,
    } from '../helpers/coupon.helpers';
    import { getLocale } from 'services/LocalizationService';
    import { RECURLY_COUPON_INVALID_CODE } from 'aa/vue/constants/shared';

    export default {
        name: 'BillingPromoSummary',
        mixins: [pricePrettyPrint, aarpMixin],
        components: {
            InputElm,
            InputField,
            ButtonCta,
        },

        props: {
            formConfig: {
                type: Object,
                default: null,
            },
            displayTrialInfo: {
                type: Boolean,
            },
            orderSummaryConfig: {
                type: Object,
                default: null,
            },
            coupon: {
                type: Object,
            },
            keydownHandler: {
                type: Function,
            },
            buttonConfig: {
                type: Object,
            },
            plan: {
                type: Object,
            },
            form: {
                type: Object,
            },
            countryClass: {
                type: String,
            },
            internationalizedPrice: {
                type: String,
            },
            showSubTotalNow: {
                type: Boolean,
            },
            showSubTotalNext: {
                type: Boolean,
            },
            showIntlZeroPrice: {
                type: Boolean,
            },
            pricing: {
                type: Object,
            },
            pageAttr: {
                type: Object,
            },
            isFTC: {
                type: Boolean,
                default: false,
            },
            trialAffectingPricing: {
                type: Boolean,
                default: false,
            },
            nextNonZeroBillingPrice: {
                type: String,
            },
        },

        data: function () {
            return {
                isPaymentPage: true,
                couponFieldManualOpen: null,
                couponFieldOpenState: null,
                lastValidCouponCode: '',
            };
        },

        methods: {
            handleClickCouponPrompt(e) {
                e.preventDefault();
                if (this.couponFieldOpenState) {
                    this.manuallyCloseCouponField();
                } else {
                    this.manuallyOpenCouponField();
                }
            },
            setStartingStateOfCouponField() {
                this.couponFieldOpenState = this.getStartingCouponFieldOpen;
            },
            manuallyOpenCouponField() {
                this.couponFieldManualOpen = true;
                this.couponFieldOpenState = true;
            },
            manuallyCloseCouponField() {
                this.couponFieldManualOpen = false;
                this.couponFieldOpenState = false;
            },

            getLocalizedTemporalUnitFromPlan() {
                //This method assumes it has the localized cadence unit from Product.php see getPriceUnit
                let localizedPlanTemporalUnit = this.plan?.priceUnit;
                if (this.isFirstLetterUpperCaseForMonthDayYear) {
                    localizedPlanTemporalUnit = capitalize(this.plan?.priceUnit);
                } else {
                    localizedPlanTemporalUnit = this.plan?.priceUnit?.toLowerCase();
                }
                return localizedPlanTemporalUnit;
            },
            replaceDurationText(subTotal, temporalAmount, temporalUnit, term) {
                let subTotalAndDuration = '';

                if (this.pageAttr.billing_summary_promotion_duration) {
                    subTotalAndDuration = this.pageAttr.billing_summary_promotion_duration;
                    subTotalAndDuration = subTotalAndDuration.replace(/{subTotal}/g, subTotal);
                    subTotalAndDuration = subTotalAndDuration.replace(
                        /{temporalAmount}/g,
                        temporalAmount,
                    );

                    const localizedPlanTemporalUnit = this.getLocalizedTemporalUnitFromPlan();
                    const singularLocalizedTemporalUnit = this.$getLocale(temporalUnit);
                    const pluralLocalizedTemporalUnit = this.$getLocale(temporalUnit + 's');

                    //pluralizeDuration conditionally adds an 's' to the end of the temporalUnit if the temporalAmount is greater than 1
                    const dynamicLocalizedTemporalUnit = this.$getLocale(
                        pluralizeDuration(temporalAmount, temporalUnit),
                    );

                    subTotalAndDuration = subTotalAndDuration.replace(
                        /{subscriptionPeriod}/g,
                        localizedPlanTemporalUnit,
                    );

                    subTotalAndDuration = subTotalAndDuration.replace(
                        /{temporalUnitSingular}/g,
                        singularLocalizedTemporalUnit,
                    );

                    subTotalAndDuration = subTotalAndDuration.replace(
                        /{temporalUnitPlural}/g,
                        pluralLocalizedTemporalUnit,
                    );

                    subTotalAndDuration = subTotalAndDuration.replace(
                        /{temporalUnit}/g,
                        dynamicLocalizedTemporalUnit,
                    );

                    subTotalAndDuration = subTotalAndDuration.replace(
                        /{discountDuration}/g,
                        `${temporalAmount} ${dynamicLocalizedTemporalUnit}`,
                    );

                    subTotalAndDuration = subTotalAndDuration.replace(/{term}/g, term);
                }

                return subTotalAndDuration;
            },
            couponIsValid(coupon) {
                if (coupon.errorMessage) {
                    return false;
                }

                return coupon?.discountString || coupon?.trialString || coupon?.recurlyInfo;
            },
            couponIsEmpty(coupon) {
                return (
                    !coupon?.discountString &&
                    !coupon?.trialString &&
                    !coupon?.recurlyInfo &&
                    !coupon?.errorMessage
                );
            },
        },

        computed: {
            ...mapState(['autoPromoChecked', 'promo']),

            isAnnualPlan() {
                return this.plan.planType === Product.TYPE_ANNUAL;
            },

            getStartingCouponFieldOpen: {
                get() {
                    if (this.isFTC) {
                        return false;
                    }
                    const defaultVal =
                        this.$store.state.promo && this.formConfig.inputList.couponCode.value;
                    return typeof this.couponFieldManualOpen == 'boolean'
                        ? this.couponFieldManualOpen
                        : defaultVal;
                },
                set(newVal) {
                    this.couponFieldManualOpen = newVal;
                },
            },

            iconCaretClassList() {
                return ['icon', 'icon--caret', this.couponFieldOpenState ? 'icon--caret--up' : ''];
            },

            currentPromoCode() {
                return this.coupon?.recurlyInfo?.code ?? '';
            },

            displayPromoField() {
                return this.promo && this.orderSummaryConfig.inputList.couponCode.value;
            },

            showCouponField() {
                // only show coupon field for Sign Up (both regular and direct link)
                const allowedRoutes = ['PAYMENT_SUMMARY', 'DIRECTLINK'];

                if (allowedRoutes.includes(this.$route.name)) {
                    return true;
                }
            },

            formatSubTotalText() {
                if (!this.coupon?.recurlyInfo) {
                    return;
                }

                const { temporal_amount, temporal_unit, single_use, duration } =
                    this.coupon.recurlyInfo;

                let priceToDisplay = this.trialAffectingPricing
                    ? this.pricing.price.next.total
                    : this.pricing.price.now.total;

                priceToDisplay = this.pricePrettyPrint(
                    priceToDisplay,
                    this.pricing.price.currency.symbol,
                    this.pricing.price.currency.code,
                );

                let subTotalAndDuration;

                if (duration === 'forever') {
                    subTotalAndDuration = this.replaceDurationText(
                        priceToDisplay,
                        this.$getLocale('forever'),
                        '',
                        '',
                    );
                } else if (temporal_unit && temporal_amount && single_use === false) {
                    if (this.plan.planType === Product.TYPE_ANNUAL && temporal_unit !== 'year') {
                        subTotalAndDuration = this.replaceDurationText(
                            priceToDisplay,
                            temporal_unit === 'month'
                                ? Math.ceil(parseInt(temporal_amount) / 12)
                                : 1,
                            'year',
                            this.pageAttr.promotion_term,
                        );
                    } else {
                        subTotalAndDuration = this.replaceDurationText(
                            priceToDisplay,
                            temporal_amount,
                            temporal_unit,
                            this.pageAttr.promotion_term,
                        );
                    }
                } else {
                    subTotalAndDuration = this.replaceDurationText(
                        priceToDisplay,
                        1,
                        this.plan.planType === Product.TYPE_ANNUAL ? 'year' : 'month',
                        this.pageAttr.promotion_term,
                    );
                }

                if (!!subTotalAndDuration) {
                    return `${subTotalAndDuration} ${this.pageAttr.billing_summary_promotion_condition}`;
                } else {
                    return '';
                }
            },
            displayBorderLine() {
                return this.promoString || this.displayTrialInfo;
            },
            isFirstLetterUpperCaseForMonthDayYear() {
                return this.$store?.state?.serverData?.isFirstLetterUpperCaseForMonthDayYear;
            },
            promoString() {
                if (!this.coupon?.recurlyInfo) {
                    return;
                }

                if (this.coupon.discountString && !this.isFTC) {
                    return this.coupon.discountString;
                }

                if (this.coupon.trialString) {
                    return '';
                }

                const shortenedTerm = this.isAnnualPlan
                    ? getLocale('year_abbr', {}, 'yr')
                    : getLocale('month_abbr', {}, 'mo');
                const offerPriceString = getOfferPriceString(
                    this.plan.currencySymbol || this.pricing.currencySymbol,
                    this.nextNonZeroBillingPrice,
                    this.plan.priceUnit,
                    shortenedTerm,
                    this.coupon.recurlyInfo,
                    this.isFTC,
                    this.isAnnualPlan,
                    this.plan.currencySubunits,
                );
                const durationString = getDurationString(
                    this.coupon.recurlyInfo,
                    this.plan.priceUnit,
                    this.isAnnualPlan,
                    this.isFTC,
                );
                return `${offerPriceString} ${durationString}`;
            },
            afterPromoDescription() {
                const shortenedTerm = this.isAnnualPlan
                    ? getLocale('year_abbr', {}, 'yr')
                    : getLocale('month_abbr', {}, 'mo');
                return getAfterPromoString(
                    this.coupon.recurlyInfo,
                    this.plan.currencySymbol,
                    this.plan.rawPrice,
                    shortenedTerm,
                    this.isFTC,
                    // we never want to show "after trial" on Payment Summary as it is displayed in the Your Plan box
                    // so we always set hasTrial to false
                    false,
                );
            },
            getCouponCodeValue() {
                return (
                    this.currentPromoCode ??
                    this.orderSummaryConfig?.inputList?.couponCode?.value ??
                    ''
                );
            },
            getCouponInputConfig() {
                return {
                    ...this.orderSummaryConfig?.inputList.couponCode,
                    value: this.getCouponCodeValue,
                };
            },
        },
        created() {
            this.setStartingStateOfCouponField();
            this.lastValidCouponCode = this.coupon?.recurlyInfo?.code ?? '';

            if (this.recurly) {
                this.getInternationalizedPricesUsingRecurly();
            }

            if (this.isAarpFlow()) {
                Object.assign(OrderSummaryConfig.inputList.couponCode, {
                    disabled: true,
                });
            }
        },
        watch: {
            'pricing.price'(newVal, oldVal) {
                if (
                    oldVal?.next?.plan !== newVal?.next?.plan &&
                    this?.plan?.rawPrice.replace(/,/g, '') === oldVal?.next?.plan &&
                    this?.plan?.planType === 'annual' &&
                    this?.coupon?.errorMessage !== RECURLY_COUPON_INVALID_CODE &&
                    (this.coupon.hasOwnProperty('errorMessage') ||
                        this.coupon.hasOwnProperty('recurlyInfo'))
                ) {
                    this.pricing.price = oldVal;
                }
            },
            'plan.recurlyCode'(newVal, oldVal) {
                // the coupon is dropped on the Monthly -> Annual toggle,
                // so re-apply the last valid code when the user switches back to monthly
                if (oldVal && !this.isFTC && !this.isAnnualPlan && !this.coupon?.recurlyInfo) {
                    if (this.lastValidCouponCode) {
                        this.$emit('reapplyCoupon', this.lastValidCouponCode);
                    }
                }
            },
            coupon(newVal, oldVal) {
                if (newVal?.recurlyInfo?.code) {
                    this.lastValidCouponCode = newVal.recurlyInfo.code;
                }

                const shouldCloseCouponForm =
                    this.couponIsValid(newVal) ||
                    // For when old coupon was valid and new coupon is empty -- we want to close / reset the form
                    (this.couponIsValid(oldVal) && this.couponIsEmpty(newVal));

                if (shouldCloseCouponForm) {
                    this.manuallyCloseCouponField();
                } else if (newVal?.errorMessage) {
                    this.manuallyOpenCouponField();
                }
            },
            recurly(newVal, oldVal) {
                if (oldVal === null && newVal !== null) {
                    this.getInternationalizedPricesUsingRecurly();
                }
            },
        },
    };
</script>
